import type { ChainStepKind, FileKind, RecordFileSource } from '@felix/domain';
import { formatDay } from '../../format.js';
import type { DocumentPermissions } from '../documents/source.js';
import type { LibraryDetail, LibraryRevision } from '../documents/types.js';
import type { PickedRecordFile } from './source.js';
import type {
  ApprovalChainDecision,
  DraftFile,
  FormRecordDetail,
  FormRecordFile,
  FormTemplate,
} from './types.js';

/**
 * Kayıt ekranlarının saf kuralları (plan M8b Task 18, 19; ADR 0042 §3, §18–§21): Actions'ın
 * düğmeleri ve nedenleri, taslak dosya listesi işlemleri, kayıt sayfasının yolu. Sunucu her
 * işlemde yetkiyi ve kuralı yeniden denetler; burada yalnız neyin çizileceği seçilir.
 */

export const FORM_RECORD_PATH = '/compliance/ism-forms/records';
export const FORM_HISTORY_PATH = '/compliance/ism-forms/history';

export function recordPath(recordId: string): string {
  return `${FORM_RECORD_PATH}/${recordId}`;
}

/** Kayıt sayfasının adresinden kayıt kimliği (`/compliance/ism-forms/records/<id>`). */
export function recordIdFromPath(path: string): string | null {
  const match = /^\/compliance\/ism-forms\/records\/([0-9a-f-]{36})\/?$/i.exec(path);
  return match?.[1] ?? null;
}

/** Kişinin ISM form yetkileri (`documents/permissions` yanıtında `forms`; gemi). */
export interface FormPermissions {
  view: boolean;
  create: boolean;
  edit: boolean;
  approve: boolean;
  return: boolean;
  void: boolean;
  export: boolean;
}

export function formPermissions(permissions: DocumentPermissions): FormPermissions | null {
  const forms = (permissions as DocumentPermissions & { forms?: FormPermissions }).forms;
  return forms ?? null;
}

/** Actions'taki kayıt düğmesi: çizilir mi, açık mı, kapalıysa nedeni (hata kodu). */
export interface ActionGate {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
}

export interface LibraryFormGates {
  openForm: ActionGate;
  printForm: ActionGate;
  fillInApp: ActionGate;
  newRecord: ActionGate;
}

const hidden: ActionGate = { visible: false, enabled: false, reason: null };
const gate = (allowed: boolean, reason: string | null): ActionGate => ({
  visible: true,
  enabled: allowed,
  reason: allowed ? null : (reason ?? 'FORBIDDEN'),
});

/**
 * Form List'teki kayıt düğmeleri (ADR 0042 §18–§21) [sorular #236, #243]:
 * - yetki (`ism_forms.create`) yoksa kayıt düğmeleri çizilmez (Download Form kalır);
 * - Open Form ana dosyası DOCX ya da XLSX olan formda açık; PDF ya da görüntüde pasif ve ipuçlu;
 * - dosyası gemide doğrulanmamış revizyonda Open Form, Print Form ve Fill in app pasif ve nedenli;
 *   New record (tarama) her zaman açık (§3);
 * - Fill in app yalnız alanlı, betiksiz, XFA'sız PDF şablonda görünür (§19); PDF indirilemez ya da
 *   pdf.js denetimi başarısız olursa gizlenmez, nedeniyle pasif görünür (`PDF_FORM_CHECK_FAILED`;
 *   ortağın geri bildirimi 2026-10-16: düğme sessizce kayboluyordu).
 */
export function libraryFormGates(input: {
  forms: FormPermissions | null;
  template: FormTemplate | null;
  /** Şablon okunamadı (ör. revizyon yürürlükte değil): kayıt yolları kapalı, neden. */
  templateError?: string | null;
  fillable: boolean;
  /** PDF alan denetimi: sürüyor ya da başarısız (indirilemedi, pdf.js açamadı). */
  fillCheck?: FillCheck;
}): LibraryFormGates {
  if (!input.forms?.create) {
    return { openForm: hidden, printForm: hidden, fillInApp: hidden, newRecord: hidden };
  }
  const template = input.template;
  if (!template) {
    const reason = input.templateError ?? 'PREVIEW_NOT_READY';
    return {
      openForm: gate(false, reason),
      printForm: gate(false, reason),
      fillInApp: hidden,
      newRecord: gate(false, reason),
    };
  }
  const office = template.source.kind === 'docx' || template.source.kind === 'xlsx';
  return {
    // Ana dosyası PDF ya da görüntü olan formda Open Form pasif; ipucu diğer yolları söyler
    openForm: office
      ? gate(template.openForm.allowed, template.openForm.reason)
      : gate(false, 'OPEN_FORM_NOT_OFFICE'),
    printForm: gate(template.printForm.allowed, template.printForm.reason),
    fillInApp:
      template.fillInApp.allowed && input.fillable
        ? gate(true, null)
        : template.source.kind === 'pdf' && !template.source.onBoard
          ? gate(false, 'FILE_NOT_ON_BOARD')
          : template.fillInApp.allowed && input.fillCheck === 'failed'
            ? gate(false, 'PDF_FORM_CHECK_FAILED')
            : hidden,
    newRecord: gate(template.newRecord.allowed, 'REVISION_NOT_CURRENT'),
  };
}

/** PDF alan denetiminin durumu; `null`: denetim yok ya da bitti. */
export type FillCheck = 'pending' | 'failed' | null;

/**
 * Formun doldurma yolu (sahip kararı 2026-10-17; ADR 0074): Word ve Excel formu Felixmate Office
 * Pack'te doldurulur (`office`); PDF ve görüntü basılır, ıslak imzalanır ve taranır (`paper`).
 */
export type FillRoute = 'office' | 'paper';

export function fillRouteOf(kind: FileKind | string | null | undefined): FillRoute {
  return kind === 'docx' || kind === 'xlsx' ? 'office' : 'paper';
}

/** Form sayfasının kayıt eylemleri (ADR 0074). */
export interface FormPageActions {
  route: FillRoute;
  /** Başlıktaki tek birincil eylem: Word/Excel'de Fill in (Open Form), PDF'te Print (Print Form). */
  primary: { action: 'open' | 'print'; gate: ActionGate };
  /** Taranmış ya da fotoğraflanmış kâğıt (New record): PDF'te başlıkta, Word'de ikincil. */
  upload: ActionGate;
  /** Word formunu kâğıtta doldurmak için kayıt numaralı baskı (yalnız `office` yolunda, ikincil). */
  paperPrint: ActionGate | null;
  /** Alanlı PDF'i uygulamada doldurma (ADR 0055): kalır, ama ana vurgu değil. */
  fillInApp: ActionGate;
  /** Şablon okunuyor: birincil yükleniyor. */
  checking: boolean;
}

/**
 * Form sayfasının eylemleri (sahip kararı 2026-10-17; ADR 0074). Kapılar `libraryFormGates`'ten
 * gelir, yeni kural yoktur; yalnız hangisinin birincil olduğu dosya türüne göre seçilir. Birincil
 * yol kapalıysa (ör. Word dosyası gemide değil) nedeniyle pasif kalır, kâğıt yolu açık kalır. Kayıt
 * açma yetkisi yoksa `null`.
 */
export function formPageActions(input: {
  gates: LibraryFormGates;
  kind: FileKind | string | null | undefined;
  checking: boolean;
}): FormPageActions | null {
  const { gates, checking } = input;
  if (!gates.newRecord.visible) return null;
  const route = fillRouteOf(input.kind);
  const pending: ActionGate = { visible: true, enabled: false, reason: null };
  const wait = (gate: ActionGate) => (checking ? pending : gate);
  return route === 'office'
    ? {
        route,
        primary: { action: 'open', gate: wait(gates.openForm) },
        upload: wait(gates.newRecord),
        paperPrint: wait(gates.printForm),
        fillInApp: hidden,
        checking,
      }
    : {
        route,
        primary: { action: 'print', gate: wait(gates.printForm) },
        upload: wait(gates.newRecord),
        paperPrint: null,
        fillInApp: gates.fillInApp,
        checking,
      };
}

/** Actions kartındaki görünür not: çeviri anahtarı ve değerleri. */
export interface RecordActionsNotice {
  key: string;
  values: Record<string, string>;
}

/**
 * Kayıt yolları kapalıyken Actions kartında görünür yazılan neden ve sonraki adım (M14b; ADR 0055
 * §1) [sorular #340]. Yalnız kayıt açma yetkisi olana ve hiçbir yolla kayıt açılamıyorsa: formun
 * yürürlükte revizyonu yoktur. Not son revizyonun durumunu söyler (taslak → gönder, onay bekliyor,
 * onaylı → yayımla, zamanlanmış → gün). İpucu (tooltip) ayrıca kalır.
 */
export function recordActionsNotice(
  detail: Pick<LibraryDetail, 'current' | 'latest' | 'scheduled'>,
  input: { create: boolean; newRecord: boolean },
): RecordActionsNotice | null {
  // Yürürlükte revizyon varsa (şablon okunuyor ya da dosya gemide değil) neden ipucundadır
  if (!input.create || input.newRecord || detail.current) return null;
  if (detail.scheduled) {
    return {
      key: 'forms.actions.notice.scheduled',
      values: {
        revision: detail.scheduled.revisionNo,
        date: formatDay(detail.scheduled.effectiveOn),
      },
    };
  }
  const latest = detail.latest;
  const state =
    latest?.state === 'draft' ||
    latest?.state === 'pending_approval' ||
    latest?.state === 'approved'
      ? latest.state
      : null;
  if (latest && state) {
    return { key: `forms.actions.notice.${state}`, values: { revision: latest.revisionNo } };
  }
  return { key: 'forms.actions.notice.none', values: {} };
}

/** Şablon kaynağı: gemi belgesi `vessel`, şirket belgesi `company` (ADR 0038 §1). */
export function revisionSourceOf(detail: {
  owner?: LibraryDetail['owner'] | undefined;
}): 'company' | 'vessel' {
  return detail.owner === 'vessel' ? 'vessel' : 'company';
}

/** Revizyonun baskı PDF'i (ADR 0028 #34(3)); yoksa `null`. */
export function printRenditionOf(
  revision: Pick<LibraryRevision, 'attachments'>,
): { id: string; sha256: string } | null {
  const file = revision.attachments.find((a) => a.role === 'print_rendition');
  return file ? { id: file.id, sha256: file.sha256 } : null;
}

/**
 * Kâğıt istisnasında seçilebilecek revizyonlar (ADR 0042 §3): aynı belgenin bir zamanlar
 * yürürlüğe girmiş, şimdi yürürlükte olmayan revizyonları. Sunucu "yürürlüğe girmiş mi"yi
 * zaman çizgisinden yeniden denetler (`REVISION_NOT_PUBLISHED`).
 */
export function paperRevisions(detail: Pick<LibraryDetail, 'revisions' | 'current'>) {
  return detail.revisions.filter(
    (r) =>
      r.id !== detail.current?.revisionId &&
      (r.state === 'superseded' || r.state === 'withdrawn') &&
      r.publishedAt !== null,
  );
}

/** Dosyanın türü (önizleyici motoru): MIME türünden, yoksa uzantıdan. */
export function fileKindOf(file: { name: string; mimeType?: string }): FileKind {
  const mime = file.mimeType ?? '';
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime.includes('spreadsheetml')) return 'xlsx';
  if (mime.includes('wordprocessingml')) return 'docx';
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'jpeg') return 'jpg';
  return (['docx', 'xlsx', 'pdf', 'jpg', 'png'] as const).find((k) => k === extension) ?? 'pdf';
}

/** Sıra numarası baştan (0…n-1): içerik `sort_order`'a göre dizilir (ADR 0042 Ek U4). */
function renumber(files: readonly DraftFile[]): DraftFile[] {
  return files.map((file, index) => ({ ...file, sortOrder: index }));
}

export function sortedFiles<T extends { sortOrder: number }>(files: readonly T[]): T[] {
  return [...files].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Taslak dosya listesine yeni dosyalar (aynı SHA-256 ikinci kez eklenmez; `DUPLICATE_FILE`). */
export function appendFiles(
  files: readonly DraftFile[],
  picked: readonly { file: PickedRecordFile['file']; source: RecordFileSource }[],
): DraftFile[] {
  const out = sortedFiles(files);
  for (const { file, source } of picked) {
    if (out.some((f) => f.sha256 === file.sha256)) continue;
    out.push({
      sortOrder: out.length,
      name: file.name,
      sha256: file.sha256,
      size: file.size,
      mimeType: file.mimeType,
      source,
    });
  }
  return renumber(out);
}

/** Dosyayı bir yukarı (-1) ya da aşağı (+1) taşır; sayfalar sırayla önizlenir (ADR 0042 §6). */
export function moveFile(files: readonly DraftFile[], index: number, step: -1 | 1): DraftFile[] {
  const out = sortedFiles(files);
  const target = index + step;
  if (index < 0 || index >= out.length || target < 0 || target >= out.length) return renumber(out);
  const [file] = out.splice(index, 1);
  if (file) out.splice(target, 0, file);
  return renumber(out);
}

export function removeFile(files: readonly DraftFile[], index: number): DraftFile[] {
  const out = sortedFiles(files);
  out.splice(index, 1);
  return renumber(out);
}

/** Son revizyonun dosyaları, değişiklik (Amend) çekmecesinin başlangıcı. */
export function latestFiles(detail: Pick<FormRecordDetail, 'files' | 'revisions'>): DraftFile[] {
  return sortedFiles(detail.files).map((f: FormRecordFile) => ({
    sortOrder: f.sortOrder,
    name: f.name,
    sha256: f.sha256,
    size: f.size,
    mimeType: f.mimeType,
    source: f.source,
  }));
}

/** Kaydın son ofis (Open Form) dosyası: çalışma kopyası ondan (§18 madde 3). */
export function lastOfficeFile(files: readonly DraftFile[]): DraftFile | null {
  return (
    sortedFiles(files)
      .filter((f) => f.source === 'office')
      .at(-1) ?? null
  );
}

/**
 * Office yolundaki kaydın Office'ten eklenmiş dosyası (kayıt sayfasının durum satırı; ADR 0074):
 * yalnız `office` doldurma yönteminde; yoksa `null`.
 */
export function officeFileOf(
  fillMethod: FormRecordDetail['fillMethod'],
  files: readonly DraftFile[],
): DraftFile | null {
  return fillMethod === 'office' ? lastOfficeFile(files) : null;
}

/**
 * Yükleme önce sorar mı (sahip kararı 2026-10-17; ADR 0074): Office'te doldurulup kapanınca
 * dosya kendiliğinden eklenir; insanlar bir de yükleyince aynı form iki kez giriyordu. Office
 * dosyası ekli Word/Excel kaydında yükleme "yine de tarama yükle?" diye sorar; kâğıt ve PDF
 * yolunda yükleme ana yoldur, sormaz.
 */
export function uploadNeedsConfirm(
  fillMethod: FormRecordDetail['fillMethod'],
  files: readonly DraftFile[],
): boolean {
  return officeFileOf(fillMethod, files) !== null;
}

/** Kayıt sayfasının taslak düzenleme alanları (RHF + Zod; ADR 0042 §1). */
export const DRAFT_TITLE_MAX = 400;
export const DRAFT_NOTE_MAX = 4000;

/**
 * Son gönderimde verilmiş onaylar (Amend uyarısı; sahip kararı 2026-10-17, ADR 0069): değişiklik
 * yeni gönderimdir, bunlar düşer ve kayıt 1. adımdan yeniden onaya gider (ADR 0042 §13).
 */
export function approvalsGiven(record: Pick<FormRecordDetail, 'chain'>): number {
  return (record.chain ?? []).reduce(
    (sum, step) =>
      sum +
      (step.decisions ?? (step.decision ? [step.decision] : [])).filter(
        (d) => d.decision === 'approved',
      ).length,
    0,
  );
}

/**
 * Onaylayanların bir satırı (ADR 0071 §4, §5): kayıt sayfasındaki adım göstergesi ve kayıt PDF'inin
 * imza bloğu aynı satırları çizer. `prepared` hazırlayanın gönderim imzasıdır.
 */
export interface SignatureRow {
  key: string;
  /** Zincirin adımı; hazırlayanın gönderim imzasında `null`. */
  step: number | null;
  kind: ChainStepKind | null;
  roleIds: string[];
  office: boolean;
  prepared: boolean;
  decision: ApprovalChainDecision | null;
  /** Sıradaki (karar bekleyen) imza. */
  current: boolean;
}

/**
 * Zincirin imza satırları: form zincirinde önce hazırlayanın gönderim imzası; "hepsi" adımında her
 * rütbe ayrı satır (kendi kararıyla), öteki adımda adımın rolleri tek satır. Eski sunucunun
 * zincirinde (rol listesi ve kararlar yok) adım başına bir satır.
 */
export function signatureRows(
  record: Pick<FormRecordDetail, 'chain' | 'pending' | 'prepared'>,
): SignatureRow[] {
  const chain = record.chain ?? [];
  const rows: SignatureRow[] = [];
  const prepared = record.prepared;
  if (prepared?.roleId && chain.length > 0) {
    rows.push({
      key: 'prepared',
      step: null,
      kind: 'prepare',
      roleIds: [prepared.roleId],
      office: false,
      prepared: true,
      decision: {
        approverId: prepared.by,
        approverRoleId: prepared.roleId,
        decision: 'approved',
        reason: null,
        at: prepared.at,
        contentHash: '',
      },
      current: false,
    });
  }
  for (const step of chain) {
    const roleIds = step.roleIds ?? [step.roleId];
    const pending = record.pending?.step === step.step;
    const base = { step: step.step, kind: step.kind ?? null, office: step.office, prepared: false };
    if (step.quorum === 'all' && roleIds.length > 1) {
      for (const role of roleIds) {
        const decision = (step.decisions ?? []).find((d) => d.approverRoleId === role) ?? null;
        rows.push({
          ...base,
          key: `${step.step}-${role}`,
          roleIds: [role],
          decision,
          current: pending && decision === null,
        });
      }
      continue;
    }
    rows.push({
      ...base,
      key: String(step.step),
      roleIds,
      decision: step.decision,
      current: pending && step.decision === null,
    });
  }
  return rows;
}

/**
 * İmza satırının başlığı: form zincirinde adımın türü ve rütbeleri ("Review · Chief Engineer"),
 * matris kuralında rolün adı. Rol adı yoksa "—".
 */
export function signatureHeading(
  row: Pick<SignatureRow, 'kind' | 'roleIds'>,
  t: (key: string) => string,
  roleName: (roleId: string) => string | undefined,
): string {
  const roles = row.roleIds.map((id) => roleName(id) ?? '—').join(' / ');
  return row.kind ? `${t(`forms.steps.kinds.${row.kind}`)} · ${roles}` : roles;
}
